import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Icon } from '@/lib/icons';
import { useApp } from '@/hooks/use-app-context';
import { useToast } from '@/hooks/use-toast';
import { complaintsApi } from '@/api/complaints.api';
import { ComplaintsTypeSelector } from './ComplaintsTypeSelector';
import type { ComplaintFormState, ComplaintType } from './complaints.types';

const controlClass =
  'h-11 rounded-2xl border-border/40 bg-background/60 px-4 text-sm text-foreground shadow-none placeholder:text-muted-foreground/50 focus-visible:ring-1 focus-visible:ring-primary/30';

const MAX_MESSAGE_LENGTH = 2000;

const INITIAL_STATE: Omit<ComplaintFormState, 'type'> = {
  name: '',
  email: '',
  phoneNumber: '',
  message: '',
};

interface ComplaintsFormProps {
  type: ComplaintType;
  onTypeChange: (type: ComplaintType) => void;
}

export function ComplaintsForm({ type, onTypeChange }: ComplaintsFormProps) {
  const { t } = useApp();
  const { toast } = useToast();
  const [form, setForm] = useState<ComplaintFormState>({ ...INITIAL_STATE, type });
  const [submitting, setSubmitting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setForm((prev) => ({ ...prev, type }));
  }, [type]);

  const autoResize = () => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = `${Math.min(el.scrollHeight, 320)}px`;
    }
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.message.trim()) {
      return toast({ title: t.complaintsMessageRequired, description: t.complaintsMessageRequiredDesc, variant: 'destructive' });
    }
    setSubmitting(true);
    try {
      await complaintsApi.submitComplaint({
        name: form.name || undefined,
        email: form.email || undefined,
        phoneNumber: form.phoneNumber || undefined,
        type: form.type,
        message: form.message,
        page: window.location.pathname,
      });
      toast({ title: t.complaintsThanks, description: t.complaintsThanksDesc });
      setForm(INITIAL_STATE);
    } catch {
      toast({ title: t.error, description: t.complaintsErrorDesc, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const remaining = MAX_MESSAGE_LENGTH - form.message.length;

  return (
    <section className="py-7">
      <div className="space-y-6">
        <div>
          <p className="flex items-center gap-3 text-[0.66rem] font-semibold uppercase tracking-[0.26em] text-primary/60">
            {t.complaintsType}
          </p>
          <div className="mt-4">
            <ComplaintsTypeSelector value={type} onChange={onTypeChange} />
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t.complaintsName}>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={t.complaintsNamePlaceholder}
              className={controlClass}
            />
          </Field>
          <Field label={t.complaintsEmail}>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder={t.complaintsEmailPlaceholder}
              className={controlClass}
            />
          </Field>
          <Field label={t.complaintsPhone}>
            <Input
              type="tel"
              value={form.phoneNumber}
              onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })}
              placeholder={t.complaintsPhonePlaceholder}
              className={controlClass}
            />
          </Field>
        </div>

        <Field label={t.complaintsMessage}>
          <div className="relative">
            <textarea
              ref={textareaRef}
              value={form.message}
              onChange={(e) => {
                if (e.target.value.length <= MAX_MESSAGE_LENGTH) {
                  setForm({ ...form, message: e.target.value });
                }
                autoResize();
              }}
              placeholder={t.complaintsMessagePlaceholder}
              rows={4}
              className="min-h-[120px] w-full resize-none rounded-2xl border border-border/40 bg-background/60 px-4 py-3 text-sm text-foreground shadow-none placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/30"
            />
            <span
              className={`absolute bottom-2 right-3 text-[0.6rem] font-semibold tabular-nums ${
                remaining < 50 ? 'text-destructive' : 'text-muted-foreground/50'
              }`}
            >
              {t.complaintsCharCount?.replace('{count}', String(form.message.length)).replace('{max}', String(MAX_MESSAGE_LENGTH)) ||
                `${form.message.length} / ${MAX_MESSAGE_LENGTH}`}
            </span>
          </div>
        </Field>

        <div className="flex justify-end pt-2">
          <Button
            type="submit"
            disabled={submitting || !form.message.trim()}
            onClick={onSubmit}
            className="h-10 rounded-full border border-primary/20 bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-none transition-colors hover:bg-primary/90 disabled:border-border/30 disabled:bg-background/40 disabled:text-muted-foreground"
          >
            {submitting ? (
              <Icon name="loader2" className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Icon name="send" className="mr-2 h-4 w-4" />
            )}
            {submitting ? t.complaintsSending : t.complaintsSend}
          </Button>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-2">
      <span className="text-[0.64rem] font-semibold uppercase tracking-[0.22em] text-muted-foreground/50">{label}</span>
      {children}
    </label>
  );
}
